import { ActionIcon, Tooltip, useMantineColorScheme } from '@mantine/core';
import { IconSun, IconMoon, IconDeviceLaptop } from '@tabler/icons-react';
import { usePreferencesStore } from '../../stores';

type ColorSchemeMode = 'light' | 'dark' | 'auto';

export default function ThemeToggleButton() {
  const { setColorScheme } = useMantineColorScheme();
  const colorSchemeMode = usePreferencesStore((state) => state.colorSchemeMode);
  const setColorSchemeMode = usePreferencesStore((state) => state.setColorSchemeMode);

  const cycleTheme = () => {
    // Cycle: auto -> light -> dark -> auto
    const modes: ColorSchemeMode[] = ['auto', 'light', 'dark'];
    const currentIndex = modes.indexOf(colorSchemeMode as ColorSchemeMode);
    const nextMode = modes[(currentIndex + 1) % 3];

    setColorSchemeMode(nextMode);

    // If not auto, set the specific color scheme
    if (nextMode !== 'auto') {
      setColorScheme(nextMode);
    } else {
      // Auto mode: detect system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setColorScheme(prefersDark ? 'dark' : 'light');
    }
  };

  const getIcon = () => {
    switch (colorSchemeMode) {
      case 'light':
        return <IconSun size={18} />;
      case 'dark':
        return <IconMoon size={18} />;
      case 'auto':
      default:
        return <IconDeviceLaptop size={18} />;
    }
  };

  const getLabel = () => {
    switch (colorSchemeMode) {
      case 'light':
        return 'Switch to dark mode';
      case 'dark':
        return 'Switch to system mode';
      case 'auto':
      default:
        return 'Switch to light mode';
    }
  };

  return (
    <Tooltip label={getLabel()} position="bottom">
      <ActionIcon
        variant="default"
        size="lg"
        onClick={cycleTheme}
        aria-label={getLabel()}
      >
        {getIcon()}
      </ActionIcon>
    </Tooltip>
  );
}
